export type Environment = {
  environment: string;
  allowCmsOverride: boolean;
  apiBaseUrl: string;
  apiPaymentsUrl: string;
  appTimeoutMillis: string | number;
  appVersionNumber: string;
  baseUrl: string;
  cmsBaseUrl: string;
  cmsUmbracoBaseUrl: string;
  cmsFallbackUrl: string;
  cookieTimeoutMillis: string;
  discoverRoutesOrigins: string[];
  externalAssetsPath: string;
  featureFlags: FeatureFlags;
  firstFlightDate: string;
  flights: FlightsConfig;
  platformType: PlatformType;
  production: boolean;
  growthBook: GrowthBookConfig;
  hotjar: HotjarConfig;
  uplift: UpliftConfig;
  tolstoy: TolstoyConfig;
  kindlyChat: KindlyChat;
  rudderstack: RudderStackConfig;
  apiEnvironments?: string;
  apexx: ApexxConfig;
  posthog: PosthogConfig;
  appInsights: AppInsightsConfig;
};

export interface AppInsightsConfig {
  correlationHeaderDomains: string[];
  instrumentationKey: string;
}

export interface PosthogConfig {
  apiKey: string;
  enabled: boolean;
  host: string;
}

export const enum PlatformType {
  web = 'Web',
  mobile = 'Mobile',
}

export interface FlightsConfig {
  lowFareRibbonDaysBeforeAndAfter: string;
  lowFareRibbonDaysBeforeAndAfterMobile: string;
}

export interface GrowthBookConfig {
  apiHost: string;
  clientKey: string;
  debug?: boolean;
  enableDevMode: string;
  subscribeToChanges?: boolean;
}

export interface HotjarConfig {
  siteId: string | number;
  version: string | number;
}

export interface RudderStackConfig {
  dataPlaneUrl: string;
  writeKey: string;
}

export interface ApexxConfig {
  rsa: string;
  scriptUrl: string;
  songbirdScriptUrl: string;
}

export enum FeatureFlagsEnum {
  CHECK_IN = 'checkIn',
  MODIFY_ALA_CARTE = 'modifyAlaCarte',
  FLIFO = 'flifo',
  USER_PROFILE = 'userProfile',
  FORGOT_PASSWORD = 'forgotPassword',
  EVERYMUNDO = 'everymundo',
  CONNECTIONS = 'connections',
  CAR_TRAWLER = 'carTrawler',
  CREDIT_FILE_ENABLED = 'creditFile',
  BUNDLE_RESTRICTION = 'bundleRestriction',
  SELF_SERVICE_UPGRADE = 'selfServiceUpgrade',
  SAVE_TO_WALLET_APPLE = 'saveToAppleWallet',
  SAVE_TO_WALLET_GOOGLE = 'saveToGoogleWallet',
  LOW_FARE_CALENDAR = 'lowFarCalendar',
  LOG_IN = 'logIn',
  GEO_HOME_BANNERS = 'geoHomeBanners',
  PLUSGRADE = 'plusgrade',
  INSURANCE_COUNTRY = 'insuranceCountry',
  HOME_FEATURED_IN = 'homeFeaturedIn',
  HOME_NEW_MAIN_CARD = 'homeNewMainCard',
  CHAT_BOT = 'chatBot',
  APPLE_PAY = 'applePay',
  CHANGE_VALIDATION = 'changeValidation',
  SHOW_PREMIUM_LIGHT = 'showPremiumLight',
  PROMO_INPUT_OPEN = 'promoInputDefaultOpen',
  KTN = 'knownTravelerNumber',
  DISRUPTION_CHECK = 'disruptionCheck',
  ALLOW_MACS = 'allowMacs',
  BRAVO_CONTENT = 'bravoContent',
  MRZ_OCR_POC = 'mrzOcr',
}

export type FeatureFlags = {
  [key in FeatureFlagsEnum]:
    | 'true'
    | ''
    | null
    | undefined
    | FeatureFlagReplacements;
};
export type FeatureFlagReplacements =
  | '#{{FEATURE-FLAG-MODIFY-ALA-CARTE}}#'
  | '#{{FEATURE-FLAG-FLIGHT-INFO}}#'
  | '#{{FEATURE-FLAG-USER-PROFILE}}#'
  | '#{{FEATURE-FLAG-CHECK-IN}}#'
  | '#{{FEATURE-FLAG-FORGOT-PASSWORD}}#'
  | '#{{FEATURE-FLAG-EVERYMUNDO}}#'
  | '#{{FEATURE-FLAG-CONNECTIONS}}#'
  | '#{{FEATURE-FLAG-CAR-TRAWLER}}#'
  | '#{{FEATURE-FLAG-CREDIT-FILE-ENABLED}}#'
  | '#{{FEATURE-FLAG-BUNDLE-RESTRICTION}}#'
  | '#{{FEATURE-FLAG-SELF-SERVICE-UPGRADE}}#'
  | '#{{FEATURE-FLAG-SAVE-TO-WALLET-APPLE}}#'
  | '#{{FEATURE-FLAG-SAVE-TO-WALLET-GOOGLE}}#'
  | '#{{FEATURE-FLAG-LOW-FARE-CALENDAR}}#'
  | '#{{FEATURE-FLAG-LOG-IN}}#'
  | '#{{FEATURE-FLAG-GEO-HOME-BANNERS}}#'
  | '#{{FEATURE-FLAG-PLUSGRADE}}#'
  | '#{{FEATURE-FLAG-INSURANCE-COUNTRY}}#'
  | '#{{FEATURE-FLAG-HOME-FEATURED-IN}}#'
  | '#{{FEATURE-FLAG-HOME-NEW-MAIN-CARD}}#'
  | '#{{FEATURE-FLAG-COVER-GENIUS}}#'
  | '#{{FEATURE-FLAG-CHAT-BOT}}#'
  | '#{{FEATURE-FLAG-APPLE-PAY}}#'
  | '#{{FEATURE-FLAG-CHANGE-VALIDATION}}#'
  | '#{{FEATURE-FLAG-SHOW-PREMIUM-LIGHT}}#'
  | '#{{FEATURE-FLAG-PROMO-INPUT-OPEN}}#'
  | '#{{FEATURE-FLAG-KNOWN-TRAVELER-NUMBER}}#'
  | '#{{FEATURE-FLAG-DISRUPTION-CHECK}}#'
  | '#{{FEATURE-FLAG-ALLOW-MACS}}#'
  | '#{{FEATURE-FLAG-MRZ-OCR}}#'
  | '#{{FEATURE-FLAG-BRAVO-CONTENT}}#';

export const FeatureFlagsCmsOverridable = [
  FeatureFlagsEnum.CHAT_BOT,
  FeatureFlagsEnum.PROMO_INPUT_OPEN,
  FeatureFlagsEnum.KTN,
] as const;

export interface UpliftConfig {
  apiKey: string;
  upCode: string;
}

export interface TolstoyConfig {
  appKey: string;
  scriptUrl: string;
}

export interface KindlyChat {
  botId: string;
  scriptUrl: string;
}

export interface ApiEnvironment {
  name: string;
  value: string;
}
